/*
 * Copyright 2026 Sony Corporation
 */

import { Box, Skeleton, Tooltip, Typography } from '@mui/material';

import { ASSET_MIME } from '../../services/asset-drag';

/**
 * One tile, whatever it came from.
 *
 * Deliberately not a Poly Haven asset: the grid renders pictures and reports
 * which one was picked, and the tab it belongs to is the only thing that knows
 * what picking means.
 */
export interface AssetTile {
  id: string;
  name: string;
  thumbnailUrl: string;
  /** Shown on hover in place of the name, where a source asks for a credit. */
  title?: string;
}

export interface AssetGridProps {
  assets: AssetTile[];
  loading: boolean;
  error: string | undefined;
  onPick: (id: string) => void;
  /** The drag payload for a tile, or undefined where a drop would mean nothing. */
  pack?: (id: string) => string | undefined;
}

const PLACEHOLDERS = Array.from({ length: 12 }, (_, index) => index);

/** How many rows are worth building at once; the rest arrive as the user scrolls. */
const PAGE = 60;

const Message = ({ children }: { children: React.ReactNode }) => (
  <Box className="flex min-h-0 flex-1 items-center px-4">
    <Typography variant="body2" color="text.secondary">
      {children}
    </Typography>
  </Box>
);

/**
 * The catalogue, as pictures.
 *
 * A plain CSS grid rather than MUI's `ImageList`: that one sizes its rows from
 * a row height we do not have, and collapses them to nothing when told `auto`.
 *
 * Thumbnails come straight from the source's CDN with whatever cache buster
 * its API already put on the URL; we never build one of these by hand.
 */
export const AssetGrid = ({ assets, loading, error, onPick, pack }: AssetGridProps) => {
  if (loading) {
    return (
      <Box className="grid min-h-0 flex-1 grid-cols-3 content-start gap-2 overflow-y-auto px-3 pb-3">
        {PLACEHOLDERS.map((index) => (
          <Skeleton key={index} variant="rounded" className="h-0 w-full pb-[100%]" />
        ))}
      </Box>
    );
  }

  if (error) {
    return <Message>{error}</Message>;
  }

  if (assets.length === 0) {
    return <Message>Nothing matches</Message>;
  }

  return (
    <Box className="grid min-h-0 flex-1 grid-cols-3 content-start gap-2 overflow-y-auto px-3 pb-3">
      {assets.slice(0, PAGE).map((asset) => (
        <Tooltip key={asset.id} title={asset.title ?? asset.name} placement="top">
          <Box
            component="button"
            aria-label={asset.name}
            onClick={() => onPick(asset.id)}
            draggable={pack !== undefined}
            onDragStart={(event: React.DragEvent) => {
              const payload = pack?.(asset.id);
              if (payload) {
                event.dataTransfer.setData(ASSET_MIME, payload);
                event.dataTransfer.effectAllowed = 'copy';
              }
            }}
            // Square by percentage padding, not by aspect-ratio: a grid row is
            // sized from the item's intrinsic height, and neither a ratio nor a
            // replaced child gives a button one — so the rows collapse and the
            // tiles overlap. Padding resolves against the column width, which
            // the track has already decided.
            className="relative block h-0 w-full cursor-pointer overflow-hidden rounded border-0 p-0 pb-[100%]"
            sx={{ bgcolor: 'action.hover', '&:hover': { outline: '2px solid', outlineColor: 'primary.main' } }}
          >
            <Box
              component="img"
              src={asset.thumbnailUrl}
              alt={asset.name}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover"
            />
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
};
